'use client';

import { UploadCloud } from 'lucide-react';

/**
 * Shared client for "repurposing bridge" free tools (podcast -> show notes,
 * transcript -> chapters, audio -> blog post). All three share the identical flow
 * — upload/URL -> transcribe via the free-audio-to-text bridge -> POST the
 * repurpose endpoint with a `mode` -> render Markdown/text output — so the only
 * per-tool differences are copy, the `mode`, and the download filename. Each tool
 * page is a thin config passed here.
 */
import { ChangeEvent, DragEvent, FormEvent, useMemo, useRef, useState } from 'react';
import Link from 'next/link';

import { MAX_AUDIO_BYTES, isAllowedAudioExtension } from '@engine/tools-shell';
import {
  completePublicUpload,
  initPublicUpload,
  uploadPublicChunks,
} from '@/lib/free-tools/public-chunked-uploader';
import { Badge } from '@/components/badge';
import {
  FreeToolTranscribeCta,
  trackFreeToolTranscribeIntent,
  useFreeToolTranscribeHandoffLinks,
} from '@/components/free-tools/free-tool-transcribe-cta';
import { Progress } from '@/components/progress';
import { cn } from '@/lib/utils';

type JobStatus = 'queued' | 'pending' | 'processing' | 'completed' | 'failed';

type OutputState = 'idle' | 'generating' | 'ready' | 'error';

export interface RepurposeToolConfig {
  /** Registry slug. Drives the CTA band's related-tool chips and the JSON-LD. */
  slug: string;
  /** Repurpose engine mode key (validated server-side). */
  mode: string;
  h1: string;
  intro: string;
  uploadLabel: string;
  submitIdle: string;
  /** Verb shown while the AI step runs, e.g. "Writing your show notes…". */
  generatingText: string;
  /** Short badge label during the AI step, e.g. "Writing notes". */
  badgeWriting: string;
  outputTitle: string;
  /** Download file name, e.g. "show-notes.md" or "chapters.txt". */
  downloadName: string;
  downloadMime: 'text/markdown' | 'text/plain';
  /** Monospace the output box (good for chapter lists). */
  mono?: boolean;
  copyLabel: string;
  ctaLabel: string;
  howItWorks: string[];
  whatYouGetTitle: string;
  whatYouGet: React.ReactNode;
  faqs: Array<{ q: string; a: string }>;
}

interface SubmitResponse {
  success: boolean;
  data?: { jobId: string; publicToken: string; handoffToken?: string };
  error?: string;
}

interface JobResponse {
  success: boolean;
  data?: { id: string; status: JobStatus; errorMessage: string | null };
  error?: string;
}

interface RepurposeResponse {
  success: boolean;
  data?: { mode: string; content: string; cached: boolean };
  error?: string;
}

const ACCEPT = '.mp4,.mp3,.wav,.m4a,.ogg,.webm,.flac,.aac,.mpeg,.mpga';

const BLOCKED_MIME_PREFIXES = ['image/'];

const UNSUPPORTED_MSG =
  'Unsupported file type. Use MP4, MP3, WAV, M4A, OGG, WEBM, FLAC, AAC, or MPEG.';

function getFriendlySubmitError(message: string | null | undefined): string {
  if (!message) return 'We could not process your file right now. Please try again.';
  const lower = message.toLowerCase();

  if (
    lower.includes('unexpected end of form') ||
    lower.includes('multipart') ||
    lower.includes('content-type')
  ) {
    return 'Upload was interrupted. Please reselect your file and try again.';
  }

  if (lower.includes('unsupported file type')) return UNSUPPORTED_MSG;

  if (lower.includes('500mb') || lower.includes('file size'))
    return 'File size exceeds 500MB free limit.';

  if (lower.includes('no file was received') || lower.includes('no file was fully uploaded')) {
    return 'No file was fully uploaded. Please reselect your file and try again.';
  }

  if (lower.includes('session expired'))
    return 'Upload session expired. Please reselect your file and try again.';

  return message;
}

export function RepurposeToolClient({ config }: { config: RepurposeToolConfig }) {
  const [file, setFile] = useState<File | null>(null);
  const [inputUrl, setInputUrl] = useState('');
  const [isDragActive, setIsDragActive] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [publicToken, setPublicToken] = useState<string | null>(null);
  const [handoffToken, setHandoffToken] = useState<string | null>(null);
  const [status, setStatus] = useState<JobStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [outputState, setOutputState] = useState<OutputState>('idle');
  const [output, setOutput] = useState('');
  const [outputError, setOutputError] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const progressValue = useMemo(() => {
    if (status === 'queued') return 10;

    if (status === 'pending') return 20;

    if (status === 'processing' && uploadProgress > 0 && uploadProgress < 100) {
      return Math.max(20, Math.min(55, Math.round(uploadProgress * 0.55)));
    }

    if (status === 'processing') return 60;

    if (status === 'completed' && outputState === 'generating') return 85;

    if (status === 'completed' && outputState === 'ready') return 100;

    return 0;
  }, [status, uploadProgress, outputState]);

  const canSubmit = Boolean(file || inputUrl.trim().length > 0) && !submitting;
  const isOutputReady = outputState === 'ready' && Boolean(output);
  const showForm = !status || status === 'failed';
  const showUploader = inputUrl.trim().length === 0;
  const showLinkInput = !file;

  const { primaryHref: transcribeHandoffHref, ready: handoffLinksReady } =
    useFreeToolTranscribeHandoffLinks(jobId, handoffToken);

  function isAllowedFile(nextFile: File): boolean {
    if (!isAllowedAudioExtension(nextFile.name)) return false;
    const mimeType = nextFile.type.toLowerCase();

    if (BLOCKED_MIME_PREFIXES.some((prefix) => mimeType.startsWith(prefix))) return false;

    return true;
  }

  function handleSelectFile(nextFile: File | null): void {
    if (nextFile && !isAllowedFile(nextFile)) {
      setFile(null);
      setError(UNSUPPORTED_MSG);

      return;
    }

    if (nextFile && nextFile.size > MAX_AUDIO_BYTES) {
      setFile(null);
      setError('File size exceeds 500MB free limit.');

      return;
    }

    setFile(nextFile);

    if (nextFile) {
      setError(null);
      setInputUrl('');
    }
  }

  function handleFileChange(event: ChangeEvent<HTMLInputElement>): void {
    handleSelectFile(event.target.files?.[0] ?? null);
  }

  function openFilePicker(): void {
    fileInputRef.current?.click();
  }

  function handleDrop(event: DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    setIsDragActive(false);
    const droppedFile = event.dataTransfer.files?.[0] ?? null;

    if (droppedFile) handleSelectFile(droppedFile);
  }

  function handleLinkChange(event: ChangeEvent<HTMLInputElement>): void {
    const nextUrl = event.target.value;
    setInputUrl(nextUrl);

    if (nextUrl.trim().length > 0) {
      setError(null);
      setFile(null);

      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function generateOutput(nextJobId: string, token: string): Promise<void> {
    setOutputState('generating');
    setOutputError(null);

    try {
      const res = await fetch(
        `/api/tools/free-audio-to-text/jobs/${nextJobId}/repurpose?mode=${encodeURIComponent(
          config.mode
        )}&token=${encodeURIComponent(token)}`,
        { method: 'POST', cache: 'no-store' }
      );

      // SAFETY: The surrounding typed contract establishes the asserted representation.
      const payload = (await res.json()) as RepurposeResponse;

      if (!res.ok || !payload.success || !payload.data) {
        setOutputState('error');
        setOutputError(payload.error ?? 'Could not generate this. Please try again.');

        return;
      }

      setOutput(payload.data.content);
      setOutputState('ready');
    } catch {
      setOutputState('error');
      setOutputError('Could not generate this. Please try again.');
    }
  }

  async function pollJob(nextJobId: string, token: string): Promise<void> {
    for (let i = 0; i < 120; i += 1) {
      const response = await fetch(
        `/api/tools/free-audio-to-text/jobs/${nextJobId}?token=${encodeURIComponent(token)}`,
        { method: 'GET', cache: 'no-store' }
      );

      // SAFETY: The surrounding typed contract establishes the asserted representation.
      const payload = (await response.json()) as JobResponse;

      if (!response.ok || !payload.success || !payload.data) {
        setError(payload.error ?? 'Failed to fetch transcription status');
        setStatus('failed');

        return;
      }

      setStatus(payload.data.status);
      setError(payload.data.errorMessage ?? null);

      if (payload.data.status === 'completed') {
        await generateOutput(nextJobId, token);

        return;
      }

      if (payload.data.status === 'failed') return;
      await new Promise((resolve) => setTimeout(resolve, 2000));
    }

    setStatus('failed');
    setError('Transcription took too long. Please try again.');
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!canSubmit) return;

    setSubmitting(true);
    setError(null);
    setStatus('pending');
    setOutputState('idle');
    setOutput('');
    setOutputError(null);
    setCopyState('idle');
    setJobId(null);
    setPublicToken(null);
    setHandoffToken(null);
    setUploadProgress(0);

    try {
      let submitData: SubmitResponse['data'] | undefined;

      if (file) {
        const session = await initPublicUpload(file);
        await uploadPublicChunks(file, session, setUploadProgress);
        submitData = await completePublicUpload(session);
      } else {
        const formData = new FormData();

        if (inputUrl.trim()) formData.set('inputUrl', inputUrl.trim());

        const response = await fetch('/api/tools/free-audio-to-text/jobs', {
          method: 'POST',
          body: formData,
        });

        // SAFETY: The surrounding typed contract establishes the asserted representation.
        const payload = (await response.json()) as SubmitResponse;

        if (!response.ok || !payload.success || !payload.data) {
          setStatus('failed');
          setError(getFriendlySubmitError(payload.error));

          return;
        }

        submitData = payload.data;
      }

      if (!submitData) {
        setStatus('failed');
        setError('We could not process your file right now. Please try again.');

        return;
      }

      setJobId(submitData.jobId);
      setPublicToken(submitData.publicToken);
      setHandoffToken(submitData.handoffToken ?? submitData.publicToken);
      setStatus('processing');
      await pollJob(submitData.jobId, submitData.publicToken);
    } catch (submitError) {
      setStatus('failed');
      setError(
        getFriendlySubmitError(
          submitError instanceof Error ? submitError.message : 'Unexpected error'
        )
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCopy(): Promise<void> {
    if (!output) return;

    try {
      await navigator.clipboard.writeText(output);
      setCopyState('copied');
      window.setTimeout(() => setCopyState('idle'), 2000);
    } catch {
      setCopyState('idle');
    }
  }

  function handleStartOver(): void {
    setFile(null);
    setInputUrl('');
    setIsDragActive(false);
    setJobId(null);
    setPublicToken(null);
    setHandoffToken(null);
    setStatus(null);
    setError(null);
    setSubmitting(false);
    setOutputState('idle');
    setOutput('');
    setOutputError(null);
    setCopyState('idle');
    setUploadProgress(0);

    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  const downloadHref = useMemo(() => {
    if (!output) return '';

    return `data:${config.downloadMime};charset=utf-8,${encodeURIComponent(output)}`;
  }, [output, config.downloadMime]);

  return (
    <div className="space-y-6">
      {/* H1 and intro live in FreeToolPageShell; only live job status stays here. */}

      {showForm ? (
        <form
          onSubmit={handleSubmit}
          className="space-y-4 rounded-[28px] border border-[rgb(var(--surface-border))] bg-[rgb(var(--surface))] p-5 sm:p-6"
        >
          {showUploader ? (
            <div className="space-y-2">
              <label className="block text-sm font-medium" htmlFor="repurpose-file">
                {config.uploadLabel}
              </label>
              <input
                id="repurpose-file"
                ref={fileInputRef}
                type="file"
                accept={ACCEPT}
                onChange={handleFileChange}
                className="sr-only"
              />
              <div
                role="button"
                tabIndex={0}
                onClick={openFilePicker}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openFilePicker();
                  }
                }}
                onDragOver={(event) => {
                  event.preventDefault();
                  setIsDragActive(true);
                }}
                onDragLeave={() => setIsDragActive(false)}
                onDrop={handleDrop}
                className={cn(
                  'flex min-h-36 w-full cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed px-4 py-5 text-center transition-colors',
                  isDragActive
                    ? 'border-violet-400/70 bg-violet-500/10'
                    : 'border-[rgb(var(--surface-border))] bg-[rgb(var(--background))] hover:border-violet-400/70 hover:bg-[rgb(var(--surface-hover))]'
                )}
              >
                <UploadCloud aria-hidden className="mb-3 h-7 w-7 text-[#C4B5FD]" />
                <p className="text-base font-semibold text-[rgb(var(--foreground))]">
                  Drag and drop your file
                </p>
                <p className="mt-1 text-sm text-[rgb(var(--foreground-secondary))]">
                  or click to browse files
                </p>
                <p className="mt-4 text-xs text-[rgb(var(--foreground-secondary))]">
                  Supported: MP4, MP3, WAV, M4A, OGG, WEBM, FLAC, AAC, MPEG. Max 10 minutes free /
                  500MB.
                </p>
              </div>
            </div>
          ) : null}

          {file ? (
            <div className="rounded-lg border border-violet-400/25 bg-[rgb(var(--surface))] p-3">
              <p className="text-xs text-[rgb(var(--foreground-secondary))]">Selected file</p>
              <p className="mt-1 break-all text-sm font-medium text-[rgb(var(--foreground))]">
                {file.name}
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={openFilePicker}
                  className="inline-flex items-center justify-center rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-white/15"
                >
                  Change file
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setFile(null);

                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  className="inline-flex items-center justify-center rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-white/15"
                >
                  Clear
                </button>
              </div>
            </div>
          ) : null}

          {showLinkInput ? (
            <details className="pt-2">
              <summary className="cursor-pointer text-sm font-medium text-[rgb(var(--foreground-secondary))]">
                Use a link instead
              </summary>
              <label className="block text-sm font-medium" htmlFor="repurpose-link">
                Or paste a YouTube or direct media link
              </label>
              <input
                id="repurpose-link"
                type="url"
                placeholder="https://youtube.com/watch?v=... or https://example.com/file.mp3"
                value={inputUrl}
                onChange={handleLinkChange}
                className="mt-2 w-full rounded-lg border border-[rgb(var(--surface-border))] bg-[rgb(var(--background))] px-4 py-3 text-sm"
              />
            </details>
          ) : null}

          {error ? <p className="text-sm text-[rgb(var(--error))]">{error}</p> : null}

          <button
            type="submit"
            disabled={!canSubmit}
            className="mt-3 inline-flex items-center justify-center rounded-full bg-[rgb(var(--tool-cta))] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[rgb(var(--tool-cta-hover))] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? 'Working...' : config.submitIdle}
          </button>
        </form>
      ) : null}

      {status && status !== 'failed' && !isOutputReady ? (
        <div className="mt-6 rounded-xl border border-[rgb(var(--surface-border))] bg-[rgb(var(--surface))] p-5">
          <Progress value={progressValue} showLabel className="mb-3" />
          <p className="text-sm text-[rgb(var(--foreground-secondary))]">
            {status === 'queued' && 'Upload queued. Waiting for worker...'}
            {status === 'pending' && 'Upload queued. Preparing transcription...'}
            {status === 'processing' && 'Transcribing your file...'}
            {status === 'completed' && outputState === 'generating' && config.generatingText}
            {status === 'completed' &&
              outputState === 'error' &&
              'Transcription done, but generation failed.'}
          </p>
          {outputState === 'error' ? (
            <div className="mt-3 space-y-2">
              <p className="text-sm text-[rgb(var(--error))]">{outputError}</p>
              {jobId && publicToken ? (
                <button
                  type="button"
                  onClick={() => generateOutput(jobId, publicToken)}
                  className="inline-flex min-h-10 items-center justify-center rounded-full border border-white/20 bg-white/10 px-4 text-sm font-semibold text-white transition-colors hover:bg-white/15"
                >
                  Retry
                </button>
              ) : null}
            </div>
          ) : null}
          {jobId ? (
            <p className="mt-2 font-mono text-[11px] text-[rgb(var(--foreground-secondary))]">
              Job {jobId}
            </p>
          ) : null}
        </div>
      ) : null}

      {status === 'failed' ? (
        <div className="mt-6 rounded-xl border border-[rgb(var(--surface-border))] bg-[rgb(var(--surface))] p-5">
          <p className="text-sm font-semibold text-[rgb(var(--error))]">
            We couldn&apos;t process your file
          </p>
          <p className="text-sm text-[rgb(var(--foreground-secondary))]">
            {error ?? 'Please try again.'}
          </p>
          <p className="text-xs text-[rgb(var(--foreground-secondary))]">
            Free mode supports audio/video files up to 10 minutes. Try reselecting the file and
            submitting again.
          </p>
        </div>
      ) : null}

      {isOutputReady ? (
        <div className="mt-8 space-y-4 rounded-xl border border-[rgb(var(--surface-border))] bg-[rgb(var(--surface))] p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-semibold">{config.outputTitle}</h2>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleCopy}
                className="inline-flex min-h-10 items-center justify-center rounded-full border border-white/20 bg-white/10 px-4 text-sm font-semibold text-white transition-colors hover:bg-white/15"
              >
                {copyState === 'copied' ? 'Copied' : config.copyLabel}
              </button>
              <a
                href={downloadHref}
                download={config.downloadName}
                className="inline-flex min-h-10 items-center justify-center rounded-full border border-white/20 bg-white/10 px-4 text-sm font-semibold text-white transition-colors hover:bg-white/15"
              >
                Download
              </a>
            </div>
          </div>
          <textarea
            readOnly
            value={output}
            rows={16}
            className={cn(
              'min-h-96 max-h-[70vh] w-full resize-y rounded-lg border border-[rgb(var(--surface-border))] bg-[rgb(var(--background))] p-4 leading-6',
              config.mono ? 'font-mono text-xs leading-5' : 'text-sm'
            )}
          />
          <p className="text-xs text-[rgb(var(--foreground-secondary))]">
            Generated with Wisprs · wisprs.co
          </p>
          <p className="text-sm text-[rgb(var(--foreground-secondary))]">
            Want full-length files, speaker labels, or editable output and exports? Import this into
            Wisprs.
          </p>
          <div className="flex flex-wrap gap-3">
            <FreeToolTranscribeCta
              jobId={jobId}
              downloadToken={handoffToken}
              freeToolKind="free-audio-to-text"
              className="w-auto px-5"
            >
              {config.ctaLabel}
            </FreeToolTranscribeCta>
            <button
              type="button"
              onClick={handleStartOver}
              className="inline-flex min-h-11 items-center justify-center rounded-full border border-white/20 bg-white/10 px-5 text-sm font-semibold text-white transition-colors hover:bg-white/15"
            >
              Start over
            </button>
          </div>
          <p className="text-xs text-[rgb(var(--foreground-secondary))]">
            Already have an account?{' '}
            <Link
              href={handoffLinksReady ? transcribeHandoffHref : '/sign-in'}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-[rgb(var(--foreground))] underline underline-offset-2"
              onClick={() => {
                if (jobId && handoffLinksReady) {
                  trackFreeToolTranscribeIntent('free-audio-to-text', jobId);
                }
              }}
            >
              Import this transcript
            </Link>{' '}
            to continue in the dashboard.
          </p>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Long-form SEO cards for this tool. Rendered by the page through FreeToolPageShell's `body`
 * slot, below the hero, so the tool, its "About"/"How this works" panel and the platform
 * chips stay together in the first view.
 */
export function RepurposeToolSeoSections({ config }: { config: RepurposeToolConfig }) {
  return (
    <>
      <div className="grid gap-4 md:grid-cols-2">
        <section className="rounded-xl border border-violet-400/20 bg-[rgb(var(--surface))] p-5">
          <h2 className="text-base font-semibold">How it works</h2>
          <ol className="mt-3 list-decimal space-y-1 pl-4 text-sm text-[rgb(var(--foreground-secondary))]">
            {config.howItWorks.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </section>
        <section className="rounded-xl border border-violet-400/20 bg-[rgb(var(--surface))] p-5">
          <h2 className="text-base font-semibold">{config.whatYouGetTitle}</h2>
          <p className="mt-3 text-sm text-[rgb(var(--foreground-secondary))]">
            {config.whatYouGet}
          </p>
        </section>
      </div>
    </>
  );
}
